import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { GRNForm } from "../../components/GRNForm";
import { grnService } from "../../services/grn.service";
import { GoodsReceiptNote } from "../../types/grn.types";

export const GRNFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const po_id = searchParams.get("po_id");

  const [grn, setGRN] = useState<GoodsReceiptNote | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = Boolean(id);
  const isCreateFromPO = Boolean(po_id);

  useEffect(() => {
    if (isEdit && id) {
      loadGRN(id);
    }
  }, [id, isEdit]);

  const loadGRN = async (grnId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await grnService.getGRN({ _id: grnId });
      if (response.success && response.data) {
        setGRN(response.data);
      } else {
        throw new Error(response.error?.message || "Failed to load GRN");
      }
    } catch (error: any) {
      console.error("Error loading GRN:", error);
      const errorMessage = error.message || "Failed to load GRN";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = (savedGRN: GoodsReceiptNote) => {
    // Navigate back to GRN list
    navigate("/business/procurement/grn");
  };

  const handleCancel = () => {
    // Navigate back to GRN list
    navigate("/business/procurement/grn");
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {isEdit ? "Loading GRN..." : "Loading..."}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
          flexDirection: "column",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error Loading GRN
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <GRNForm
      grn={grn}
      po_id={po_id || undefined}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};
